import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"

import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { finishAndGetNextStep, useSessionConfig, useSessionDetail, useStepWithMedia } from "@/services/dexie"

import { ClientRedirect } from "../../../components/ClientRedirect"
import { getStepRoute, LINKS } from "../../../routing/links"
import { useStepsRouting } from "../../../routing/useStepsRouting"
import { Cash4CarCaseNumber } from "../../../sections/extraClientData/Cash4CarCaseNumber"
import { GeneraliCaseNumber } from "../../../sections/extraClientData/GeneraliCaseNumber"
import { SitCaseNumber } from "../../../sections/extraClientData/SitCaseNumber"
import { ORGANIZATIONS } from "../../../utils/constants"

export const Route = createFileRoute("/$sessionId/_session/extra-client-data")({
  component: ExtraClientData,
})

function ExtraClientData() {
  const { sessionId } = Route.useParams()
  const { data: config, isLoading: configLoading } = useSessionConfig(sessionId)
  const { data: detail, isLoading: detailLoading } = useSessionDetail(sessionId)

  const { data: step, isLoading: stepLoading } = useStepWithMedia(sessionId, "ExtraClientData")
  const { firstUnfinishedStepLink, isLoading: stepsLoading } = useStepsRouting(sessionId)

  if (configLoading || detailLoading || stepLoading || stepsLoading || !config || !detail) {
    return <SpinnerLayout />
  }

  if (step?.isFinished) {
    return <ClientRedirect to={firstUnfinishedStepLink ?? LINKS.finalCheck(sessionId)} />
  }

  switch (config.organizationHumanReadableId) {
    case ORGANIZATIONS.cash4car:
      return (
        <Cash4CarCaseNumber organizationHumanReadableId={config.organizationHumanReadableId} caseId={detail.caseId} />
      )
    case ORGANIZATIONS.generali:
      return (
        <GeneraliCaseNumber
          organization={ORGANIZATIONS.generali}
          organizationHumanReadableId={config.organizationHumanReadableId}
          caseId={detail.caseId}
        />
      )
    case ORGANIZATIONS.sitmpCz:
      return <SitCaseNumber organizationHumanReadableId={config.organizationHumanReadableId} caseId={detail.caseId} />
    case ORGANIZATIONS.instacover:
    case ORGANIZATIONS.kooperativa:
    case ORGANIZATIONS.direct:
    default:
      return <ExtraClientDataNoForm sessionId={sessionId} />
  }
}

function ExtraClientDataNoForm({ sessionId }: { sessionId: string }) {
  const navigate = useNavigate()

  useEffect(() => {
    finishAndGetNextStep("ExtraClientData", sessionId).then((nextStep) => {
      navigate({ to: nextStep ? getStepRoute(nextStep, sessionId) : LINKS.root(sessionId), replace: true })
    })
  }, [sessionId, navigate])

  return null
}
